import React, { FunctionComponent, useRef, useState } from "react";
import { Box, DialogActions, DialogContent, Toolbar } from "@mui/material";
import { ITrack } from "../../../hooks/albums";
import { strings } from "../../../localization/LocalizedStrings";
import { ContainedButton } from "../../ContainedButton";
import { TrackItem } from "../TrackItem";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import styles from "../../albums/AlbumList/styles.module.scss";
import { arrayMoveMutable } from "array-move";
import { AlertDialog } from "../../AlertDialog";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { v4 as uuidV4 } from "uuid";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";
import { flushSync } from "react-dom";
import { ContentUnavailableNotice } from "../ContentUnavailableNotice";

export const TrackListDialog: FunctionComponent<ITrackListDialogProps> = ({
  trackList,
  open,
  setOpen,
  fetchFromDiscogsEnabled,
  onChange,
  onFetchFromDiscogs
}) => {
  const [tracks, setTracks] = useState(trackList ?? []);
  const [didMakeUpdates, setDidMakeUpdates] = useState(false);
  const bottomReference = useRef<HTMLHeadingElement>(null);
  const [isOpenFetchFromDiscogsConfirmationAlert, setIsOpenFetchFromDiscogsConfirmationAlert] =
    useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const scrollToBottom = () => {
    bottomReference.current?.scrollIntoView({ behavior: "smooth" });
  };
  const resetData = () => {
    setTracks(trackList ?? []);
  };
  const onCancel = () => {
    if (didMakeUpdates) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      handleClose();
    }
  };
  const handleClose = () => {
    setDidMakeUpdates(false);
    setOpen(false);
  };
  const onSubmit = () => {
    onChange(tracks);
    handleClose();
  };
  const addTrack = () => {
    flushSync(() => {
      setTracks(tracks => [
        ...tracks,
        {
          order: -1,
          name: strings.generic_track_name
        }
      ]);
    });
    setDidMakeUpdates(true);
    scrollToBottom();
  };
  return (
    <PiperDialog
      fullWidth
      maxWidth={"md"}
      open={open}
      onClose={onCancel}
      PaperProps={{ style: { height: "100%" } }}
    >
      <Toolbar style={{ zIndex: 1000 }}>
        <h3 style={{ marginRight: 8 }}>{strings.track_list_title}</h3>
        <ToolbarButton
          disabled={!fetchFromDiscogsEnabled}
          onClick={() => setIsOpenFetchFromDiscogsConfirmationAlert(true)}
          tooltip={
            fetchFromDiscogsEnabled
              ? strings.fetch_from_discogs
              : strings.fetch_from_discogs_disabled
          }
          iconType={IconType.Download}
        />
        <ToolbarButton
          onClick={addTrack}
          tooltip={strings.add_track}
          iconType={IconType.AddCircle}
        />
        <Box flexGrow={1} />
        <ToolbarButton onClick={onCancel} tooltip={strings.close} iconType={IconType.Close} />
      </Toolbar>
      <DragDropContext
        onDragEnd={result => {
          if (!result.destination || result.source.index == result.destination.index) {
            return;
          }
          arrayMoveMutable(tracks, result.source.index, result.destination.index);
        }}
      >
        <DialogContent dividers>
          {tracks.length > 0 ? (
            <Droppable droppableId={"trackList"}>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {tracks.map((track, index) => (
                    <Draggable key={uuidV4()} draggableId={`draggable.${index}`} index={index}>
                      {provided => (
                        <div
                          className={styles.albumItemContainer}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <TrackItem
                            track={track}
                            onUpdate={() => setDidMakeUpdates(true)}
                            onDelete={() => {
                              const newTracks = tracks.filter((_, i) => i !== index);
                              setTracks(newTracks);
                              setDidMakeUpdates(true);
                            }}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  <div ref={bottomReference} />
                </div>
              )}
            </Droppable>
          ) : (
            <ContentUnavailableNotice
              isLoading={false}
              items={tracks}
              emptyIconType={IconType.Genres}
              emptyTitle={strings.empty_tracklist}
              emptyDescription={strings.empty_attribute_description}
              searchText={undefined}
              canAdd
              addButtonTitle={strings.add_track}
              onAddItem={addTrack}
            />
          )}
        </DialogContent>
      </DragDropContext>
      <DialogActions>
        <ContainedButton type={"submit"} onClick={onSubmit}>
          {strings.save}
        </ContainedButton>
      </DialogActions>
      <AlertDialog
        message={strings.fetch_tracks_from_discogs_confirmation}
        open={isOpenFetchFromDiscogsConfirmationAlert}
        setOpen={setIsOpenFetchFromDiscogsConfirmationAlert}
        isConfirm={true}
        onConfirm={() => {
          onFetchFromDiscogs();
          handleClose();
        }}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={() => {
          resetData();
          handleClose();
        }}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
    </PiperDialog>
  );
};

export interface ITrackListDialogProps {
  trackList?: ITrack[];
  open: boolean;
  setOpen: (state: boolean) => void;
  fetchFromDiscogsEnabled: boolean;
  onChange: (newTrackList: ITrack[]) => void;
  onFetchFromDiscogs: () => void;
}
